import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { Plus as PlusIcon } from '../../assets/icons/plus';
import { styles } from './styles';
import CustomTable from '../../components/common/CustomTable';
import { LocationsFilter } from '../../features/locations/Locations/LocationFilter';
import { columns } from './columns';
import PermissionChecker from '../../components/common/PermissionChecker';
import {
    clearFilters,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    setFilters,
    setInitialFilters,
} from '../../store/slices/tableController';
import { useSearchLocationsMutation, useListLocationsMutation } from '../../api/locations';

const Locations = (props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchLocations, { isLoading: isSearchLoading }] = useSearchLocationsMutation();
    const [listLocations, { isLoading: isListLoading }] = useListLocationsMutation();
    const controller = useSelector((state) => state.tableController);
    const [locations, setLocations] = useState([]);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        dispatch(
            setInitialFilters([
                {
                    property: {
                        name: 'name',
                        type: 'string',
                    },
                    operator: {
                        value: '',
                        typesAccepted: [],
                    },
                    value: '',
                },
            ]),
        );
    }, []);

    const fetchData = useCallback(
        async (searched, filters, sort, sortBy, page, pageSize) => {
            try {
                const query = new URLSearchParams({});
                if (filters.length) {
                    query.append('c', filters[0].property);
                    query.append('q', filters[0].value);
                }
                if (sortBy) {
                    query.append('sort', sortBy);
                    query.append('order', sort);
                }
                query.append('offset', page * pageSize);
                query.append('max', pageSize);

                let response;
                if (searched) {
                    response = await searchLocations(query).unwrap();
                } else {
                    response = await listLocations(query).unwrap();
                }

                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setLocations(response);
            } catch (err) {
                if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
                else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
            }
        },
        [searchLocations, listLocations, dispatch, enqueueSnackbar],
    );

    useEffect(() => {
        if (fetchData)
            fetchData(
                controller.searched,
                controller.filters,
                controller.sort,
                controller.sortBy,
                controller.page,
                controller.pageSize,
            );
    }, [
        controller.searched,
        controller.filters,
        controller.sort,
        controller.sortBy,
        controller.page,
        controller.pageSize,
        fetchData,
    ]);

    const handleFiltersApply = (newFilters) => {
        const parsedFilters = newFilters.map((filter) => ({
            property: filter.property.name,
            value: filter.value,
            operator: filter.operator.value,
        }));

        dispatch(setFilters(parsedFilters));
    };

    const handleFiltersClear = () => {
        dispatch(clearFilters());
    };

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleRowClick = (row) => {
        navigate(`/settings/locations/${row}`);
    };

    const isLoading = isSearchLoading || isListLoading;

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Locations</Typography>

                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_CREATE_LOCATION']}
                    noAuthRedirect={false}
                >
                    <Grid item xs="auto">
                        <Button
                            color="primary"
                            onClick={() => navigate('/settings/locations/add')}
                            size="large"
                            startIcon={<PlusIcon fontSize="small" />}
                            variant="contained"
                        >
                            Add Location
                        </Button>
                    </Grid>
                </PermissionChecker>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <LocationsFilter
                            disabled={false}
                            filters={controller.filters}
                            onFiltersApply={handleFiltersApply}
                            onFiltersClear={handleFiltersClear}
                            filterProperties={columns}
                            initialFilters={controller.initialFilters}
                        />
                        <CustomTable
                            onRowClick={handleRowClick}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onSortChange={handleSortChange}
                            page={controller.page + 1}
                            columns={columns}
                            rows={locations.locationInstanceList}
                            rowsCount={locations.locationInstanceCount}
                            sort={controller.sort}
                            sortBy={controller.sortBy}
                            showRowSelector={false}
                            showPagignation
                            keyColumn="id"
                            action={() =>
                                fetchData(
                                    controller.searched,
                                    controller.filters,
                                    controller.sort,
                                    controller.sortBy,
                                    controller.page,
                                    controller.pageSize,
                                )
                            }
                        />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default Locations;
